/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */

import 'react';
import Dot from 'comps/Dot';
import { css, jsx } from '@emotion/core';
import { Fragment, useState, useEffect } from 'react';
import type { DotTypeT, DotStyleT } from 'comps/Dot';
import useCommands from '../../Left2/useCommands';
import Toggle from 'material-ui/Toggle';
export type LegendTypeT = 'gradient' | 'category' | 'custom' | 'markup';
import history from 'lib/history';
import useContextState from '../../Left2/useContextState';
import _ from 'lodash';

const TITLE_CATEGORY_LINKER = {
  'BUSINESSES & ATTRACTIONS': 'businesses',
  'DEVELOPMENT ACTIVITY': 'developments',
  'COMMERCIAL REAL ESTATE': 'listings',
  'STREETSCAPE ASSETS': 'streetscapeAssets',
  'STREETSCAPE ISSUES': 'issues',
  UNITS: 'units',
};
const CLEAR_NAME = 'Clear All Data';

type ColorT = string;

type PanelItemT = {
  dotStyle: DotStyleT,
  dotType: DotTypeT,
  color: ColorT,
  label: string,
  uuid: string,
  category_type: string,
};

type PropsT = {
  title: string,
  items?: PanelItemT[],
  markup?: string,
  type: LegendTypeT,
  gradient?: ColorT[],
  prefaceItems?: PanelItemT[],
  uuid: string,
  category_type: string,
  internalUpdate: string,
  historyFilters: any,
  setInternalUpdate: Function,
};

function useLayerActive(id) {
  const contextState = useContextState();
  return contextState && contextState.layersActiveLayers.has(id);
}

function useFilterSync(filters: {}) {
  const cmd = useCommands();
  useEffect(() => {
    cmd.updateGlobalFilters(filters);
  }, [filters]);
}

/*eslint-disable*/
function hexToRgbA(hex, opacity) {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = `0x${c.join('')}`;
    // $FlowFixMe[unsafe-addition]
    const r = (c >> 16) & 255; // eslint-disable-line
    // $FlowFixMe[unsafe-addition]
    const g = (c >> 8) & 255; // eslint-disable-line
    // $FlowFixMe[unsafe-addition]
    const b = c & 255; // eslint-disable-line
    const rgb = [r, g, b].join(',');
    return `rgba(${rgb}, ${opacity})`;
  }
  return hex;
}
/* eslint-enable*/

const mkListItem = (v, i, type) => (
  <li className={`legend-layer-item ${type}`} key={i}>
    <Dot
      containerStyle={{ top: -1 }}
      dotStyle={v.dotStyle}
      type={v.dotType}
      color={v.color}
    />
    {` ${v.label}`}
  </li>
);

const MkListItemCustom = ({
  item,
  index,
  type,
  setInternalUpdate,
  internalUpdate,
}) => {
  const cmd = useCommands();
  const checked = useLayerActive(item.uuid);
  return (
    <li
      className={`legend-layer-item ${type}`}
      key={index}
      css={css(`
        text-transform: uppercase;
        font-weight: 300;
      `)}
    >
      <Toggle
        label={
          <div>
            <Dot
              containerStyle={{ top: -1 }}
              dotStyle={item.dotStyle}
              type={item.dotType}
              color={item.color}
            />
            {` ${item.label}`}
          </div>
        }
        defaultToggled={false}
        style={{ flex: 1 }}
        trackStyle={{ backgroundColor: '#A6B7C3' }}
        thumbSwitchedStyle={{ backgroundColor: '#fff' }}
        trackSwitchedStyle={{ backgroundColor: '#0FE886' }}
        onToggle={() => {
          if (internalUpdate) return null;
          setInternalUpdate(true);
          cmd.layerToggle(item.uuid);
        }}
        toggled={checked}
      />
    </li>
  );
};

const InteractiveCategoryList = (items, type, changeFilter, subSection) => {
  const disabledColor = '#C4C4C4';
  const categoryList = items.map((v, i) => {
    const active = subSection.includes(v.label) || subSection.length < 1;
    let mainColor = active ? v.color : disabledColor;
    if (mainColor === 'black') mainColor = '#000000';
    return (
      <li
        onClick={() => {
          changeFilter(v.label);
        }}
        className={`legend-layer-item ${type}`}
        key={i}
        css={css(`
        cursor: pointer;
        border-radius: 3px;
        margin: 5px 5px 5px 0;
        padding: 5px 7px;
        color: ${active ? 'black' : 'rgba(52,52,52,0.6)'};
        background-color: ${hexToRgbA(mainColor, 0.15)};
      `)}
      >
        <Dot
          containerStyle={{ top: -1 }}
          dotStyle="fill"
          type="circle"
          color={mainColor}
        />{' '}
        {v.label}
      </li>
    );
  });
  return (
    <div
      css={css(`
      display: flex;
      flex-wrap: wrap;
    `)}
    >
      {categoryList}
    </div>
  );
};

const MarkupListItem = ({ markup }) => (
  <li className="legend-layer-item markup">
    <div dangerouslySetInnerHTML={{ __html: markup }} />
  </li>
);

const GradientListItem = ({ gradient }) => (
  <li className="legend-layer-item gradient">
    <div
      className="legend-gradient"
      style={{
        opacity: 1,
        borderRadius: 5,
        height: 8,
        background: `linear-gradient(90deg , ${gradient.join(', ')})`,
      }}
    />
  </li>
);

const PrefaceListItems = ({ items, type }) => {
  if (!items) return null;
  if (items && items.length === 0) return null;
  return (
    <Fragment>
      <li style={{ margin: '5px 0' }} key={0} />
      {items.map((v, i) => mkListItem(v, i + 1, type))}
      <li
        style={{ borderBottom: 'solid 1px #ccc', margin: '5px 0' }}
        key={items.length + 2}
      />
    </Fragment>
  );
};

const Title = ({ title, isSwitch, uuid, setInternalUpdate }) => {
  const cmd = useCommands();
  const checked = useLayerActive(uuid);
  return (
    <li
      className="legend-panel-title"
      css={css(`
        text-transform: uppercase;
        font-weight: 300;
        display: flex;
        justify-content: space-between;
      `)}
    >
      {isSwitch ? (
        <Toggle
          label={title}
          defaultToggled={false}
          style={{ flex: 1 }}
          trackStyle={{ backgroundColor: '#A6B7C3' }}
          thumbSwitchedStyle={{ backgroundColor: '#fff' }}
          trackSwitchedStyle={{ backgroundColor: '#0FE886' }}
          onToggle={() => {
            setInternalUpdate(true);
            cmd.layerToggle(uuid);
          }}
          toggled={checked}
        />
      ) : (
        title
      )}
    </li>
  );
};

export default ({
  title,
  items,
  markup,
  type = 'category',
  gradient,
  prefaceItems,
  category_type,
  uuid,
  setInternalUpdate,
  internalUpdate,
  historyFilters,
}: PropsT): Element<string> => {
  let listItems;
  const [filters, setFilters] = useState(history.getQuery().filter);
  useFilterSync(filters);
  const { filter } = history.getQuery();
  const fullFilter = filter || {};
  const titleUppercase: any = title ? title.toUpperCase() : '';
  const subName =
    titleUppercase in TITLE_CATEGORY_LINKER
      ? TITLE_CATEGORY_LINKER[titleUppercase]
      : '';
  const section = fullFilter[subName] || {};
  const subSection = section[type] || [];

  function changeFilter(changeName) {
    if (internalUpdate) return null;
    let currentType = type;
    let currentSubCategory = subSection;
    const isCategory = _.get(historyFilters, [subName, type], []).includes(
      changeName
    );
    const statuses = _.get(historyFilters, [subName, 'status'], []) || [];
    try {
      const isStatus = statuses.includes(changeName);
      if (!isCategory && !isStatus) return null;
      if (isStatus) {
        currentType = 'status';
        currentSubCategory = section[currentType] || [];
      }
    } catch (e) {
      window.Rollbar && window.Rollbar.error(e);
      return null;
    }
    setInternalUpdate(true);
    let newFilters = currentSubCategory.includes(changeName)
      ? currentSubCategory.filter((c) => c !== changeName)
      : [...currentSubCategory, changeName];
    if (!newFilters.length) {
      newFilters = [CLEAR_NAME];
    } else if (newFilters.includes(CLEAR_NAME) && newFilters.length > 1) {
      newFilters = newFilters.filter((item) => item !== CLEAR_NAME);
    }
    const allFilters = {
      ...fullFilter,
      [String(subName)]: {
        ...section,
        [String(currentType)]: newFilters,
      },
    };
    setFilters(allFilters);
  }

  if (type === 'category' && items) {
    if (category_type === 'layer') {
      listItems = items.map((v, i) => mkListItem(v, i, type));
    } else {
      listItems = InteractiveCategoryList(
        items,
        type,
        changeFilter,
        subSection
      );
    }
  } else if (type === 'custom' && items) {
    listItems = items.map((v, i) => (
      <MkListItemCustom
        key={i + 1}
        item={v}
        index={i + 1}
        type={type}
        setInternalUpdate={setInternalUpdate}
        internalUpdate={internalUpdate}
      />
    ));
  } else if (type === 'gradient' && gradient) {
    listItems = <GradientListItem gradient={gradient} />;
  } else if (type === 'markup' && markup) {
    listItems = <MarkupListItem markup={markup} />;
  }

  return (
    <ul
      className="legend-panel list-unstyled"
      css={css(`
        margin: 0 5px 0 0;
        padding: 5px 0 5px 8px;
        width: 220px;
    `)}
    >
      <Title
        title={title}
        isSwitch={category_type === 'layer'}
        uuid={uuid}
        setInternalUpdate={setInternalUpdate}
      />
      <PrefaceListItems items={prefaceItems} type={type} />
      {listItems}
    </ul>
  );
};
